// FAQ.jsx
// Drop into your src/pages/ folder.
// Matches OurCars / Gallery dark luxury theme exactly.

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";

// const API_BASE = "http://localhost:5000/api";
const API_BASE = "http://192.168.0.57:5000/api";
// ─── MOCK FALLBACK ────────────────────────────────────────────
const mockFaqs = [
  { faq_id:1, faq_title:"What documents do I need to rent a car?", faq_desc:"You will need a valid driving licence, a government-issued photo ID (passport or national ID), and a credit or debit card in the driver's name. International customers may also require an International Driving Permit (IDP) in addition to their home-country licence." },
  { faq_id:2, faq_title:"Is there a minimum age requirement to rent?", faq_desc:"The minimum age to rent a vehicle is 21 years. Drivers between 21 and 24 may be subject to a young driver surcharge depending on the vehicle category. Drivers must hold a valid licence for at least 1 year." },
  { faq_id:3, faq_title:"What is included in the daily rental rate?", faq_desc:"Our base daily rate includes unlimited kilometres, basic third-party liability insurance, 24/7 roadside assistance, and vehicle registration. Optional add-ons such as CDW, additional insurance, GPS, and child seats are available at extra cost." },
  { faq_id:4, faq_title:"Can I modify or cancel my booking?", faq_desc:"Yes. Bookings can be modified or cancelled up to 24 hours before the scheduled pick-up time at no charge. Cancellations within 24 hours may incur a one-day rental fee. Please use the 'Modify Reservation' page or contact our support team." },
  { faq_id:5, faq_title:"Do you offer airport pick-up and drop-off?", faq_desc:"Yes, we offer pick-up and drop-off at all major airports. Simply select your airport location when making a booking. Night delivery charges may apply for collections outside of standard business hours (9 PM – 6 AM)." },
  { faq_id:6, faq_title:"What fuel policy applies to my rental?", faq_desc:"All vehicles are provided with a full tank of fuel and must be returned full. If the vehicle is returned with less fuel, a refuelling fee will be charged at our posted rates, which are higher than pump prices." },
  { faq_id:7, faq_title:"Is there a security deposit required?", faq_desc:"Yes, a refundable security deposit is held on your credit card at the time of vehicle collection. The amount varies by vehicle category — typically between ₹5,000 and ₹25,000. The hold is released within 7–10 business days of return, subject to no damage or outstanding charges." },
  { faq_id:8, faq_title:"Can I add an additional driver?", faq_desc:"Yes. Additional drivers can be added at the rental counter. The additional driver must be present with their valid driving licence. A small daily fee applies per additional driver, which covers them under the same insurance terms as the primary renter." },
];

// ─── CSS ─────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --gold: #C9A84C;
    --gold-light: #E8C97A;
    --dark: #0A0A0F;
    --dark2: #111118;
    --dark3: #1A1A24;
    --surface: #16161F;
    --border: rgba(201,168,76,0.18);
    --text: #E8E8F0;
    --muted: #7A7A9A;
    --white: #FFFFFF;
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: var(--dark); color: var(--text); font-family: 'DM Sans', sans-serif; }

  /* ── NAV ── */
  .fq-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 5%; height: 72px;
    background: rgba(10,10,15,0.96); backdrop-filter: blur(18px);
    border-bottom: 1px solid var(--border);
  }
  .fq-nav-logo { font-family: 'Playfair Display', serif; font-size: 1.6rem; color: var(--gold); letter-spacing: -0.5px; cursor: pointer; }
  .fq-nav-logo span { color: var(--white); }
  .fq-nav-links { display: flex; gap: 2rem; list-style: none; }
  .fq-nav-links a { color: var(--muted); text-decoration: none; font-size: .9rem; font-weight: 500; letter-spacing: .5px; transition: color .2s; }
  .fq-nav-links a:hover, .fq-nav-links a.active { color: var(--gold); }
  .fq-nav-cta { background: var(--gold); color: var(--dark); font-weight: 600; font-size: .85rem; padding: .55rem 1.4rem; border: none; border-radius: 8px; cursor: pointer; transition: background .2s; }
  .fq-nav-cta:hover { background: var(--gold-light); }
  .fq-mobile-toggle { display: none; flex-direction: column; gap: 5px; cursor: pointer; background: none; border: none; padding: 4px; }
  .fq-mobile-toggle span { display: block; width: 24px; height: 2px; background: var(--gold); border-radius: 2px; transition: all .3s; }
  .fq-mobile-menu {
    display: none; position: fixed; top: 72px; left: 0; right: 0; z-index: 999;
    background: rgba(10,10,15,0.98); backdrop-filter: blur(18px);
    border-bottom: 1px solid var(--border); padding: 1.5rem 5%; flex-direction: column;
  }
  .fq-mobile-menu.open { display: flex; }
  .fq-mobile-menu a { color: var(--muted); text-decoration: none; font-size: 1rem; padding: .9rem 0; border-bottom: 1px solid var(--border); display: block; transition: color .2s; }
  .fq-mobile-menu a:hover { color: var(--gold); }
  .fq-mobile-menu .fq-nav-cta { margin-top: 1rem; width: 100%; text-align: center; padding: .7rem; }

  /* ── HERO ── */
  .fq-hero {
    padding: 130px 5% 70px; background: var(--dark2);
    border-bottom: 1px solid var(--border);
    position: relative; overflow: hidden;
  }
  .fq-hero::before {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(ellipse 60% 80% at 30% 60%, rgba(201,168,76,.08) 0%, transparent 70%);
    pointer-events: none;
  }
  .fq-hero-inner { position: relative; display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 2rem; }
  .fq-hero-label { font-size: .72rem; letter-spacing: 3px; text-transform: uppercase; color: var(--gold); margin-bottom: .8rem; }
  .fq-hero-title { font-family: 'Playfair Display', serif; font-size: clamp(2.2rem, 5vw, 4rem); color: var(--white); line-height: 1.08; margin-bottom: 1rem; }
  .fq-hero-title em { color: var(--gold); font-style: italic; }
  .fq-hero-desc { color: var(--muted); font-size: .95rem; line-height: 1.75; max-width: 500px; }
  .fq-hero-stat { text-align: right; }
  .fq-hero-stat-num { font-family: 'Playfair Display', serif; font-size: 3.5rem; color: var(--gold); line-height: 1; }
  .fq-hero-stat-label { font-size: .78rem; letter-spacing: 2px; text-transform: uppercase; color: var(--muted); margin-top: .3rem; }

  /* ── SEARCH BAR ── */
  .fq-search-bar {
    background: var(--surface); border-bottom: 1px solid var(--border);
    padding: 1.2rem 5%; display: flex; align-items: center; gap: 1rem;
    position: sticky; top: 72px; z-index: 100;
    backdrop-filter: blur(16px);
  }
  .fq-search-wrap { position: relative; flex: 1; max-width: 520px; }
  .fq-search-wrap svg { position: absolute; left: .9rem; top: 50%; transform: translateY(-50%); color: var(--muted); pointer-events: none; }
  .fq-search {
    width: 100%; padding: .65rem 1rem .65rem 2.5rem;
    background: var(--dark3); border: 1px solid var(--border);
    border-radius: 10px; color: var(--text); font-size: .88rem; font-family: inherit;
    outline: none; transition: border-color .2s;
  }
  .fq-search:focus { border-color: var(--gold); }
  .fq-search::placeholder { color: var(--muted); }
  .fq-search-clear {
    position: absolute; right: .75rem; top: 50%; transform: translateY(-50%);
    background: none; border: none; color: var(--muted); cursor: pointer;
    font-size: 1rem; line-height: 1; padding: 2px; transition: color .2s;
  }
  .fq-search-clear:hover { color: var(--gold); }
  .fq-result-count { font-size: .82rem; color: var(--muted); white-space: nowrap; }
  .fq-result-count strong { color: var(--gold); }
  .fq-expand-all {
    margin-left: auto; background: transparent; border: 1px solid var(--border);
    color: var(--muted); padding: .5rem 1.1rem; border-radius: 8px;
    font-size: .8rem; font-weight: 600; cursor: pointer; font-family: inherit;
    transition: all .2s; white-space: nowrap;
  }
  .fq-expand-all:hover { border-color: var(--gold); color: var(--gold); }

  /* ── LAYOUT ── */
  .fq-body { padding: 3rem 5% 5rem; max-width: 900px; margin: 0 auto; }

  /* ── ACCORDION ── */
  .fq-item {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: 14px; margin-bottom: 1rem; overflow: hidden;
    transition: border-color .3s, box-shadow .3s;
  }
  .fq-item.open {
    border-color: rgba(201,168,76,0.45);
    box-shadow: 0 8px 40px rgba(0,0,0,.3);
  }
  .fq-item-header {
    width: 100%; background: none; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: space-between; gap: 1rem;
    padding: 1.35rem 1.6rem; text-align: left; font-family: inherit;
  }
  .fq-item-header:hover .fq-q-text { color: var(--gold); }
  .fq-q-num {
    font-family: 'Playfair Display', serif; font-size: 1rem;
    color: var(--gold); opacity: .5; flex-shrink: 0; min-width: 28px;
    transition: opacity .2s;
  }
  .fq-item.open .fq-q-num { opacity: 1; }
  .fq-q-text {
    flex: 1; font-size: .97rem; font-weight: 600; color: var(--text);
    line-height: 1.4; transition: color .2s;
  }
  .fq-item.open .fq-q-text { color: var(--white); }
  .fq-chevron {
    width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
    background: var(--dark3); border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center;
    color: var(--muted); transition: all .3s;
  }
  .fq-item.open .fq-chevron {
    background: var(--gold); border-color: var(--gold); color: var(--dark); transform: rotate(180deg);
  }
  .fq-answer-wrap {
    max-height: 0; overflow: hidden;
    transition: max-height .4s cubic-bezier(.4,0,.2,1), padding .3s;
  }
  .fq-answer-wrap.open { max-height: 600px; }
  .fq-answer {
    padding: 0 1.6rem 1.5rem 4rem;
    color: var(--muted); font-size: .92rem; line-height: 1.8;
    border-top: 1px solid var(--border);
  }
  .fq-answer-inner { padding-top: 1.2rem; }

  /* Highlight matched search text */
  .fq-highlight { background: rgba(201,168,76,.22); color: var(--gold-light); border-radius: 3px; padding: 0 2px; }

  /* ── EMPTY ── */
  .fq-empty { text-align: center; padding: 5rem 2rem; }
  .fq-empty-icon { font-size: 3rem; opacity: .35; margin-bottom: 1rem; }
  .fq-empty h3 { font-family: 'Playfair Display', serif; font-size: 1.5rem; color: var(--white); margin-bottom: .5rem; }
  .fq-empty p { color: var(--muted); font-size: .9rem; }
  .fq-empty button {
    margin-top: 1.5rem; background: var(--gold); color: var(--dark);
    font-weight: 700; padding: .65rem 1.8rem; border: none;
    border-radius: 8px; cursor: pointer; font-size: .9rem; font-family: inherit;
  }

  /* ── LOAD MORE ── */
  .fq-load-wrap { text-align: center; margin-top: 2.5rem; }
  .fq-load-btn {
    background: transparent; border: 1px solid var(--gold); color: var(--gold);
    padding: .7rem 2.4rem; border-radius: 10px; font-size: .9rem; font-weight: 600;
    cursor: pointer; font-family: inherit; transition: all .25s;
  }
  .fq-load-btn:hover { background: var(--gold); color: var(--dark); transform: translateY(-2px); box-shadow: 0 10px 30px rgba(201,168,76,.25); }
  .fq-load-btn:disabled { opacity: .4; cursor: not-allowed; transform: none; box-shadow: none; }

  /* ── SKELETON ── */
  .fq-skeleton {
    background: linear-gradient(90deg, var(--surface) 25%, var(--dark3) 50%, var(--surface) 75%);
    background-size: 200% 100%; animation: fqShimmer 1.5s infinite;
    border-radius: 14px; height: 68px; margin-bottom: 1rem;
  }
  @keyframes fqShimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

  /* ── CTA BANNER ── */
  .fq-cta {
    margin-top: 4rem; border-radius: 20px;
    background: var(--dark3); border: 1px solid var(--border);
    padding: 2.8rem 2.5rem; display: flex; align-items: center; justify-content: space-between;
    gap: 2rem; flex-wrap: wrap; position: relative; overflow: hidden;
  }
  .fq-cta::before {
    content: ''; position: absolute; right: -60px; top: -60px;
    width: 220px; height: 220px; border-radius: 50%;
    background: radial-gradient(circle, rgba(201,168,76,.12) 0%, transparent 70%);
    pointer-events: none;
  }
  .fq-cta-label { font-size: .72rem; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold); margin-bottom: .5rem; }
  .fq-cta h3 { font-family: 'Playfair Display', serif; font-size: 1.6rem; color: var(--white); margin-bottom: .5rem; }
  .fq-cta p { color: var(--muted); font-size: .9rem; line-height: 1.6; }
  .fq-cta-btn {
    background: var(--gold); color: var(--dark); font-weight: 700;
    padding: .75rem 2rem; border: none; border-radius: 10px;
    font-size: .95rem; cursor: pointer; font-family: inherit; white-space: nowrap;
    transition: all .25s; flex-shrink: 0;
  }
  .fq-cta-btn:hover { background: var(--gold-light); transform: translateY(-2px); box-shadow: 0 10px 30px rgba(201,168,76,.3); }

  /* ── RESPONSIVE ── */
  @media (max-width: 768px) {
    .fq-nav-links, .fq-nav-cta { display: none; }
    .fq-mobile-toggle { display: flex; }
    .fq-hero { padding: 100px 4% 50px; }
    .fq-hero-inner { flex-direction: column; align-items: flex-start; }
    .fq-hero-stat { text-align: left; }
    .fq-body { padding: 2rem 4% 4rem; }
    .fq-search-bar { padding: .9rem 4%; }
    .fq-answer { padding: 0 1.2rem 1.2rem 1.2rem; }
  }
  @media (max-width: 480px) {
    .fq-item-header { padding: 1.1rem 1.2rem; }
    .fq-q-num { display: none; }
    .fq-cta { padding: 2rem 1.5rem; }
    .fq-cta h3 { font-size: 1.3rem; }
    .fq-expand-all { display: none; }
  }
`;

// ─── NAV ─────────────────────────────────────────────────────
// function FqNavbar({ siteName }) {
//   const [menuOpen, setMenuOpen] = useState(false);
//   const navigate = useNavigate();
//   const links = [
//     { label: "Home",    path: "/" },
//     { label: "Fleet",   path: "/our-cars" },
//     { label: "Gallery", path: "/gallery" },
//     { label: "FAQs",    path: "/faqs" },
//     { label: "Contact", path: "/contact-us" },
//   ];
//   return (
//     <>
//       <nav className="fq-nav">
//         <div className="fq-nav-logo" onClick={() => navigate("/")}>
//           Orisnz<span>{siteName?.replace("Drive", "") || "-Fleet"}</span>
//         </div>
//         <ul className="fq-nav-links">
//           {links.map(item => (
//             <li key={item.label}>
//               <a href={item.path} className={item.label === "FAQs" ? "active" : ""}
//                 onClick={e => { e.preventDefault(); navigate(item.path); }}>
//                 {item.label}
//               </a>
//             </li>
//           ))}
//         </ul>
//         <button className="fq-nav-cta" onClick={() => navigate("/#bknengine")}>Book Now</button>
//         <button className="fq-mobile-toggle" onClick={() => setMenuOpen(o => !o)} aria-label="Toggle menu">
//           <span style={menuOpen ? { transform: "rotate(45deg) translate(5px,5px)" } : {}} />
//           <span style={menuOpen ? { opacity: 0 } : {}} />
//           <span style={menuOpen ? { transform: "rotate(-45deg) translate(5px,-5px)" } : {}} />
//         </button>
//       </nav>
//       <div className={`fq-mobile-menu${menuOpen ? " open" : ""}`}>
//         {links.map(item => (
//           <a key={item.label} href={item.path}
//             onClick={e => { e.preventDefault(); navigate(item.path); setMenuOpen(false); }}>
//             {item.label}
//           </a>
//         ))}
//         <button className="fq-nav-cta" onClick={() => { setMenuOpen(false); navigate("/#bknengine"); }}>Book Now</button>
//       </div>
//     </>
//   );
// }


// ─── HIGHLIGHT HELPER ────────────────────────────────────────
function Highlighted({ text = "", query = "" }) {
  if (!query.trim()) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase()
          ? <mark key={i} className="fq-highlight">{part}</mark>
          : part
      )}
    </>
  );
}

// ─── ACCORDION ITEM ──────────────────────────────────────────
function FaqItem({ faq, index, isOpen, onToggle, searchQuery }) {
  const answerRef = useRef(null);
  return (
    <div className={`fq-item${isOpen ? " open" : ""}`}>
      <button className="fq-item-header" onClick={onToggle} aria-expanded={isOpen}>
        <span className="fq-q-num">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="fq-q-text">
          <Highlighted text={faq.faq_title} query={searchQuery} />
        </span>
        <span className="fq-chevron" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      <div className={`fq-answer-wrap${isOpen ? " open" : ""}`} ref={answerRef}>
        <div className="fq-answer">
          <div className="fq-answer-inner">
            <Highlighted text={faq.faq_desc} query={searchQuery} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────
const LIMIT = 10;

export default function FAQ() {
  const navigate = useNavigate();

  const [allFaqs,    setAllFaqs]    = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [loadingMore,setLoadingMore]= useState(false);

  const [search,     setSearch]     = useState("");
  const [activeSearch, setActiveSearch] = useState("");

  const [openIds,    setOpenIds]    = useState(new Set());
  const [allExpanded,setAllExpanded]= useState(false);
  const [settings,   setSettings]   = useState({});

  const searchTimer = useRef(null);

  // Settings
  useEffect(() => {
    fetch(`${API_BASE}/settings`).then(r => r.json()).then(d => setSettings(d || {})).catch(() => {});
  }, []);

  // Fetch FAQs
  const fetchFaqs = useCallback((pg, append = false) => {
    if (!append) setLoading(true); else setLoadingMore(true);
    fetch(`${API_BASE}/faqs?page=${pg}&limit=${LIMIT}`)
      .then(r => r.json())
      .then(d => {
        const rows = Array.isArray(d?.data) ? d.data : Array.isArray(d) ? d : mockFaqs;
        const tot  = d?.total ?? rows.length;
        setAllFaqs(prev => append ? [...prev, ...rows] : rows);
        setTotal(tot);
      })
      .catch(() => { if (!append) setAllFaqs(mockFaqs); setTotal(mockFaqs.length); })
      .finally(() => { setLoading(false); setLoadingMore(false); });
  }, []);

  useEffect(() => { fetchFaqs(1, false); }, [fetchFaqs]);

  // Debounced search
  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setActiveSearch(val), 350);
  };

  // Client-side filtering (FAQs are typically small enough)
  const filtered = activeSearch.trim()
    ? allFaqs.filter(f =>
        f.faq_title.toLowerCase().includes(activeSearch.toLowerCase()) ||
        f.faq_desc.toLowerCase().includes(activeSearch.toLowerCase())
      )
    : allFaqs;

  const hasMore = allFaqs.length < total && !activeSearch.trim();

  const toggle = (id) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allExpanded) {
      setOpenIds(new Set());
      setAllExpanded(false);
    } else {
      setOpenIds(new Set(filtered.map(f => f.faq_id)));
      setAllExpanded(true);
    }
  };

  // Sync allExpanded state
  useEffect(() => {
    if (filtered.length && openIds.size === filtered.length) setAllExpanded(true);
    else setAllExpanded(false);
  }, [openIds, filtered]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchFaqs(next, true);
  };

  return (
    <>
      <style>{css}</style>
      {/* <FqNavbar siteName={settings?.site_name} /> */}
      <Navbar activePage="FAQ" siteName={settings?.site_name} />
      {/* ── HERO ── */}
      <div className="fq-hero">
        <div className="fq-hero-inner">
          <div>
            <div className="fq-hero-label">Everything You Need to Know</div>
            <h1 className="fq-hero-title">Frequently Asked<br /><em>Questions</em></h1>
            <p className="fq-hero-desc">
              Can't find an answer? Reach out to our support team and we'll get back to you within the hour.
            </p>
          </div>
          {total > 0 && (
            <div className="fq-hero-stat">
              <div className="fq-hero-stat-num">{total}</div>
              <div className="fq-hero-stat-label">Answers Ready</div>
            </div>
          )}
        </div>
      </div>

      {/* ── SEARCH BAR ── */}
      <div className="fq-search-bar">
        <div className="fq-search-wrap">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="fq-search"
            placeholder="Search questions…"
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
          />
          {search && (
            <button className="fq-search-clear" onClick={() => { setSearch(""); setActiveSearch(""); }}>×</button>
          )}
        </div>

        <div className="fq-result-count">
          <strong>{filtered.length}</strong> {filtered.length === 1 ? "result" : "results"}
        </div>

        {filtered.length > 0 && (
          <button className="fq-expand-all" onClick={toggleAll}>
            {allExpanded ? "Collapse All" : "Expand All"}
          </button>
        )}
      </div>

      {/* ── ACCORDION BODY ── */}
      <div className="fq-body">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="fq-skeleton" style={{ animationDelay: `${i * 0.07}s`, height: i % 3 === 0 ? "68px" : "68px" }} />
          ))
        ) : filtered.length === 0 ? (
          <div className="fq-empty">
            <div className="fq-empty-icon">🔍</div>
            <h3>No Results Found</h3>
            <p>Try different keywords or browse all questions below.</p>
            <button onClick={() => { setSearch(""); setActiveSearch(""); }}>Clear Search</button>
          </div>
        ) : (
          <>
            {filtered.map((faq, i) => (
              <FaqItem
                key={faq.faq_id}
                faq={faq}
                index={i}
                isOpen={openIds.has(faq.faq_id)}
                onToggle={() => toggle(faq.faq_id)}
                searchQuery={activeSearch}
              />
            ))}

            {/* Load more (only when not filtering) */}
            {hasMore && (
              <div className="fq-load-wrap">
                <button className="fq-load-btn" onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading…" : `Load More  ·  ${total - allFaqs.length} remaining`}
                </button>
              </div>
            )}
          </>
        )}

        {/* ── CONTACT CTA ── */}
        {!loading && (
          <div className="fq-cta">
            <div>
              <div className="fq-cta-label">Still have questions?</div>
              <h3>We're here to help</h3>
              <p>Our support team is available 24/7. Reach out and we'll respond within the hour.</p>
            </div>
            <button className="fq-cta-btn" onClick={() => navigate("/contact-us")}>
              Contact Support →
            </button>
          </div>
        )}
      </div>
    </>
  );
}